import React, { useEffect, useState } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  Loader2,
  X,
  RefreshCw,
  MapPin,
  Check,
  ArrowRight,
  Map as MapIcon,
  Share2,
} from 'lucide-react';
import { api } from '../../utils/api';
import Stamp from './Stamp';
import FakeQR from './FakeQR';
import { resolveStampIcon } from './registry';
import { getRank } from '../../utils/tiers';
import { useLocalizedNavigate } from '../../i18n/useLocalizedNav';

type ClaimResult = {
  previousMultiplier: number;
  newMultiplier: number;
  firstTime: boolean;
  uniqueStamps: number;
};

// Fixed decorative confetti dots — purely visual, no animation.
const DOTS = Array.from({ length: 36 }, (_, i) => {
  const r = (n: number) => {
    const x = Math.sin(i * 99 + n) * 10000;
    return x - Math.floor(x);
  };
  const palette = ['#ffc888', '#81c784', '#e05c53', '#a39189'];
  return {
    top: `${(r(1) * 100).toFixed(1)}%`,
    left: `${(r(2) * 100).toFixed(1)}%`,
    size: 5 + Math.round(r(3) * 7),
    color: palette[Math.floor(r(4) * palette.length)],
  };
});

type Props = {
  locationId: string;
  locationName: string;
  region?: string | null;
  category: string;
  stampId: string;
  stampName: string;
  onClose: () => void;
};

const SESSION_SECONDS = 60;

const randCode = () =>
  Math.random().toString(36).slice(2, 5).toUpperCase();

const ClaimOverlay: React.FC<Props> = ({
  locationId,
  locationName,
  region,
  category,
  stampId,
  stampName,
  onClose,
}) => {
  const navigate = useLocalizedNavigate();

  // Stamp medallion size is pixel-based, so resize it responsively
  // (Tailwind classes can't drive the SVG's width/height).
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Icon of the stamp being claimed — shown in the QR center.
  const renderStampIcon = resolveStampIcon(stampName, locationName, category);

  // Current multiplier for this stamp (null = not yet collected).
  const [currentMult, setCurrentMult] = useState<number | null>(null);
  const [loadingMult, setLoadingMult] = useState(true);

  // Session / QR lifecycle. `expired` is derived from the countdown.
  const [seed, setSeed] = useState(() => Date.now());
  const [secondsLeft, setSecondsLeft] = useState(SESSION_SECONDS);
  const expired = secondsLeft <= 0;
  const [session, setSession] = useState(() => [
    randCode(),
    randCode(),
    'LV' + Math.floor(Math.random() * 9 + 1),
  ]);

  // Inline staff keypad.
  const [keypad, setKeypad] = useState(false);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Set once the claim succeeds → switches the overlay to the success view.
  const [claimResult, setClaimResult] = useState<ClaimResult | null>(null);

  // Fetch this stamp's current multiplier on open.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const mine = await api.get('/stamps/my');
        if (!alive) return;
        if (Array.isArray(mine)) {
          const hit = mine.find((s) => s.id === stampId);
          setCurrentMult(hit?.multiplier ?? null);
        }
      } catch {
        /* leave as not-collected */
      } finally {
        if (alive) setLoadingMult(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [stampId]);

  // Countdown — pause while keypad is open, stop at 0 (expired).
  useEffect(() => {
    if (keypad || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, keypad]);

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const regenerate = () => {
    setSeed(Date.now());
    setSession([randCode(), randCode(), 'LV' + Math.floor(Math.random() * 9 + 1)]);
    setSecondsLeft(SESSION_SECONDS);
  };

  const nextMultiplier = (currentMult ?? 0) + 1;
  const multLine = loadingMult
    ? 'Checking your passport…'
    : currentMult == null
      ? 'Not yet collected · this becomes ×1'
      : `Already ${currentMult}× collected · this becomes ×${nextMultiplier}`;

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post('/validate/claim', {
        location_id: locationId,
        pin,
      });
      if (res.error) setError(res.error);
      else
        setClaimResult({
          previousMultiplier: res.previousMultiplier,
          newMultiplier: res.newMultiplier,
          firstTime: res.firstTime,
          uniqueStamps: res.uniqueStamps,
        });
    } catch {
      setError('Connection failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Success view ---------------------------------------------------
  if (claimResult) {
    const r = claimResult;
    const rank = getRank(r.uniqueStamps);
    const visitOrdinal =
      r.newMultiplier === 1
        ? 'first visit'
        : r.newMultiplier === 2
          ? 'second visit'
          : `${r.newMultiplier}th visit`;

    const body = r.firstTime
      ? `${stampName} has been added to your passport — your first visit to this venue.`
      : `${stampName} multiplier is now ×${r.newMultiplier} — ${visitOrdinal} to this venue.`;
    const rankLine = rank.nextTier
      ? ` ${rank.toNext} more ${rank.toNext === 1 ? 'stamp' : 'stamps'} until ${rank.nextTier.name}.`
      : ' You have reached the top tier — Guru of Armenia.';

    return (
      <div className="fixed inset-0 z-50 bg-warm-100 text-warm-800 overflow-y-auto">
        {/* Static decorative dots */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {DOTS.map((d, i) => (
            <span
              key={i}
              className="absolute rounded-full"
              style={{
                top: d.top,
                left: d.left,
                width: d.size,
                height: d.size,
                background: d.color,
                opacity: 0.5,
              }}
            />
          ))}
        </div>

        <div className="relative container mx-auto px-5 py-6 lg:px-6 lg:py-12 min-h-full flex flex-col">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-warm-200/60 hover:bg-warm-200 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-warm-600" />
            </button>
          </div>

          {/* Mobile: single column, stamp right under the headline.
              Desktop: 2-col grid, stamp on the right. */}
          <div className="flex-1 flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
            {/* Badge + headline — order 1 */}
            <div className="order-1 lg:col-start-1 lg:row-start-1 space-y-3 lg:space-y-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-brand-success/20 text-brand-success font-heading font-bold text-[10px] lg:text-xs tracking-[0.15em] uppercase rounded-full">
                <Check className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                Stamp claimed · Verified
              </span>
              <h1 className="font-display text-4xl lg:text-7xl text-warm-800 leading-[1.05] lg:leading-[1.02]">
                Another <span className="italic text-brand-accent">story</span>,
                sealed.
              </h1>
            </div>

            {/* Earned stamp — mobile order 2 (under headline), desktop right */}
            <div className="order-2 lg:col-start-2 lg:row-span-4 flex justify-center">
              <Stamp
                name={stampName}
                locationName={locationName}
                category={category}
                earned
                multiplier={r.newMultiplier}
                size={isDesktop ? 340 : 200}
              />
            </div>

            {/* Body copy — mobile order 3 */}
            <p className="order-3 lg:col-start-1 lg:row-start-2 text-warm-300 text-base lg:text-lg leading-relaxed max-w-lg">
              {body}
              {rankLine}
            </p>

            {/* Stats — mobile order 4 */}
            <div className="order-4 lg:col-start-1 lg:row-start-3 bg-white rounded-2xl border border-warm-200/70 p-4 lg:p-5 grid grid-cols-3 gap-3 lg:gap-4">
              <div>
                <p className="font-heading font-bold text-[9px] lg:text-[10px] tracking-[0.15em] text-warm-300 uppercase">
                  Venue
                </p>
                <p className="font-bold text-warm-800 text-xs lg:text-sm mt-1">
                  {[locationName, region].filter(Boolean).join(' · ')}
                </p>
              </div>
              <div>
                <p className="font-heading font-bold text-[9px] lg:text-[10px] tracking-[0.15em] text-warm-300 uppercase">
                  Multiplier
                </p>
                <p className="font-bold text-brand-accent text-xs lg:text-sm mt-1">
                  {r.previousMultiplier > 0
                    ? `×${r.previousMultiplier} → ×${r.newMultiplier}`
                    : `×${r.newMultiplier}`}
                </p>
              </div>
              <div>
                <p className="font-heading font-bold text-[9px] lg:text-[10px] tracking-[0.15em] text-warm-300 uppercase">
                  Rank
                </p>
                <p className="font-bold text-warm-800 text-xs lg:text-sm mt-1">
                  {rank.tier.name.replace(' Member', '')}
                  {rank.nextTier
                    ? ` · ${r.uniqueStamps}/${rank.nextTier.min}`
                    : ''}
                </p>
              </div>
            </div>

            {/* Actions — mobile order 5 */}
            <div className="order-5 lg:col-start-1 lg:row-start-4 flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3 lg:gap-4">
              <button
                onClick={() => {
                  onClose();
                  navigate('/my-stamps');
                }}
                className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 lg:px-7 lg:py-4 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all"
              >
                View my passport
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate('/locations');
                }}
                className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-5 py-3.5 lg:px-7 lg:py-4 bg-white border border-warm-200 text-warm-800 font-heading font-bold rounded-xl hover:bg-warm-100 transition-all"
              >
                <MapIcon className="w-4 h-4" />
                Plan next visit
              </button>
              <button
                type="button"
                /* Instagram share wired up with the mobile pass — placeholder for now. */
                className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 font-heading font-bold text-warm-600 hover:text-brand-accent transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>

          <p className="font-heading font-bold text-[10px] lg:text-xs tracking-[0.2em] text-warm-300/60 uppercase mt-5">
            Aranor Passport · Stamp Claimed
          </p>
        </div>
      </div>
    );
  }

  const pct = (secondsLeft / SESSION_SECONDS) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-brand-secondary text-white overflow-y-auto">
      <div className="min-h-full container mx-auto px-5 py-5 lg:py-8 flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-brand-primary rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-brand-secondary" />
            </div>
            <span className="font-display text-lg text-brand-primary">
              Aranor
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile: single column in requested order. Desktop: 2-col grid. */}
        <div className="flex-1 flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center mt-4 lg:mt-0">
          {/* Header — order 1 */}
          <div className="order-1 lg:col-start-1 lg:row-start-1 space-y-2 lg:space-y-4">
            <p className="font-heading font-bold text-xs lg:text-sm tracking-[0.25em] text-brand-accent uppercase">
              Claim Code · {expired ? 'Expired' : 'Active'}
            </p>
            <h1 className="font-display text-4xl lg:text-6xl leading-[1.05]">
              {(() => {
                const [first, ...rest] = locationName.split(/\s+/);
                return (
                  <>
                    <span className="text-brand-primary">{first}</span>{' '}
                    <span className="italic text-white">
                      {rest.join(' ')}
                    </span>
                  </>
                );
              })()}
            </h1>
            <p className="flex items-center gap-2 text-warm-300 text-sm">
              <MapPin className="w-4 h-4 shrink-0" />
              {[locationName, region].filter(Boolean).join(' · ')} · {category}
            </p>
          </div>

          {/* QR — mobile order 2, desktop right column */}
          <div className="order-2 lg:col-start-2 lg:row-span-3 flex flex-col items-center gap-3">
            <div className="relative">
              <div
                className={`bg-white rounded-2xl lg:rounded-3xl p-4 lg:p-6 shadow-2xl ${
                  expired ? 'opacity-30' : 'animate-pulse'
                }`}
              >
                <FakeQR
                  seed={seed}
                  className="w-52 h-52 sm:w-60 sm:h-60 lg:w-80 lg:h-80"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-12 h-12 lg:w-14 lg:h-14 bg-brand-primary rounded-xl flex items-center justify-center text-brand-secondary shadow-lg">
                    {renderStampIcon({ width: 28, height: 28 })}
                  </div>
                </div>
              </div>
              {expired && (
                <button
                  onClick={regenerate}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <span className="flex items-center gap-2 px-5 py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all">
                    <RefreshCw className="w-4 h-4" />
                    Regenerate code
                  </span>
                </button>
              )}
            </div>
            <div className="text-center space-y-0.5">
              <p className="font-mono text-xs lg:text-sm tracking-[0.2em] text-warm-300">
                SESSION · {session[0]} · {session[1]} · {session[2]}
              </p>
              <p className="text-warm-300 text-[10px] lg:text-xs">
                Single-use · regenerates every {SESSION_SECONDS}s
              </p>
            </div>
          </div>

          {/* Live timer — mobile order 3 */}
          <div className="order-3 lg:col-start-1 lg:row-start-2 space-y-2">
            <div className="flex items-center justify-between text-xs lg:text-sm">
              <span className="flex items-center gap-2 font-heading font-bold tracking-wider uppercase">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    expired ? 'bg-warm-500' : 'bg-brand-danger animate-pulse'
                  }`}
                />
                {expired
                  ? 'Session expired'
                  : `Live · expires in ${secondsLeft}s`}
              </span>
              <span className="font-mono text-warm-300">
                00:{secondsLeft.toString().padStart(2, '0')}
              </span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-linear"
                style={{
                  width: `${expired ? 0 : pct}%`,
                  background: 'linear-gradient(90deg,#81c784,#ffc888,#e05c53)',
                }}
              />
            </div>
          </div>

          {/* Actions — mobile order 4 */}
          <div className="order-4 lg:col-start-1 lg:row-start-3">
            {!keypad ? (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <button
                    onClick={() => setKeypad(true)}
                    disabled={expired}
                    className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-3 text-sm lg:text-base whitespace-nowrap bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all disabled:opacity-50"
                  >
                    <ShieldCheck className="w-4 h-4 shrink-0" />
                    Activate
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-3 text-sm lg:text-base bg-white/5 hover:bg-white/10 font-heading font-bold rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-warm-300 text-xs lg:text-sm max-w-md leading-relaxed">
                  Activation opens the staff verification keypad. Once they
                  confirm your visit, your stamp is sealed and the multiplier
                  increments.
                </p>
              </div>
            ) : (
              <form onSubmit={handleClaim} className="space-y-3 max-w-sm">
                <p className="font-heading font-bold text-xs tracking-[0.2em] text-brand-accent uppercase">
                  Staff verification
                </p>
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  placeholder="ABC123"
                  maxLength={6}
                  autoFocus
                  aria-label="Staff verification code"
                  className="w-full text-center text-3xl tracking-[0.4em] py-3.5 bg-white/5 rounded-2xl focus:ring-2 focus:ring-brand-primary outline-none font-mono uppercase placeholder:normal-case placeholder:tracking-normal"
                  value={pin}
                  onChange={(e) =>
                    setPin(
                      e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, '')
                        .slice(0, 6),
                    )
                  }
                />
                {error && (
                  <div className="flex items-center gap-2 text-brand-danger font-bold text-sm">
                    <ShieldAlert className="w-4 h-4" />
                    {error}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setKeypad(false);
                      setPin('');
                      setError('');
                    }}
                    className="flex-1 py-3 bg-white/5 hover:bg-white/10 font-bold rounded-xl transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || pin.length !== 6}
                    className="flex-1 py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl hover:bg-brand-accent transition-all disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="animate-spin mx-auto w-5 h-5" />
                    ) : (
                      'Confirm visit'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Stamp info — mobile order 5, desktop under actions */}
          <div className="order-5 lg:col-start-1 lg:row-start-4 bg-white/[0.04] border border-white/5 rounded-2xl p-4 flex items-center gap-4">
            <Stamp
              name={stampName}
              locationName={locationName}
              category={category}
              earned
              size={80}
              className="shrink-0"
            />
            <div className="space-y-0.5 min-w-0">
              <p className="font-heading font-bold text-[10px] tracking-[0.2em] text-warm-300 uppercase">
                You'll earn
              </p>
              <p className="font-display text-xl lg:text-2xl text-brand-primary truncate">
                {stampName}
              </p>
              <p className="text-warm-300 text-xs lg:text-sm">{multLine}</p>
            </div>
          </div>
        </div>

        <p className="order-last font-heading font-bold text-[10px] lg:text-xs tracking-[0.2em] text-warm-300/60 uppercase mt-5">
          Aranor Passport · Claim Session
        </p>
      </div>
    </div>
  );
};

export default ClaimOverlay;
